/**
 * #746 1.2: the one Resolution control. The Close dialog (Update panel) is the only place a resolution is written:
 * the draft (Riley / Desk / checklist / summary), Regenerate, the "reviewed" tick, the invoice-unsafe notice and the
 * disclosure warnings all live here. State stays with the caller (the close transaction owns it); this is the view.
 */
import { Button, Checkbox, Textarea } from '@patchbay/ui'
import { Loader2, RefreshCw, Sparkles } from 'lucide-react'

export const RESOLUTION_WARNING: Record<string, string> = {
  contractor_identity: 'A contractor identity may be visible to the customer.',
  forbidden_word: 'The word “contractor” is customer-facing.',
  internal_name: 'An internal system name is customer-facing.',
  carrier_name: 'An upstream carrier name is customer-facing.',
  money: 'A dollar amount will print on the customer invoice.',
  commercial_reference: 'A PO, WO, or invoice number will print on the customer invoice.',
  cost_or_price: 'Internal cost or price language will print on the customer invoice.',
  second_person: 'Write about the customer in third person (“Customer requested…”), not to them as “you/your”.',
  guard_unavailable: 'The resolution disclosure check is unavailable; review the text carefully.',
}

export const STRIPPED_LABEL: Record<string, string> = {
  money: 'prices',
  commercial_reference: 'PO, WO or invoice numbers',
  cost_or_price: 'cost or price wording',
  contractor_identity: 'contractor names or contacts',
  forbidden_word: 'the word contractor',
  carrier_name: 'carrier names',
  internal_name: 'internal system names',
  second_person: 'second-person you/your wording',
}

export type ResolutionDraft =
  | { state: 'idle' | 'loading' }
  | {
      state: 'ready'
      source: 'riley' | 'summary' | 'desk' | 'workflow' | 'alert' | 'none'
      detail?: string | null
      /** #512: invoice-unsafe details the API removed from the generated text (codes, never the text). */
      stripped?: string[]
    }

export function ResolutionField({
  value,
  onChange,
  draft,
  warnings,
  reviewRequired,
  untouchedRiley,
  untouchedAlert = false,
  signedOff,
  onSignedOff,
  onRegenerate,
}: {
  value: string
  onChange: (text: string) => void
  draft: ResolutionDraft
  warnings: string[]
  /** An untouched Riley draft needs an explicit review tick; an edit counts as the review. */
  reviewRequired: boolean
  untouchedRiley: boolean
  /** #1182: the alert template is ready to close; editing it is the review. */
  untouchedAlert?: boolean
  signedOff: boolean
  onSignedOff: (next: boolean) => void
  onRegenerate: () => void
}) {
  return (
    <div className="space-y-2" data-testid="resolution-field">
      <label className="block space-y-1 text-sm font-medium">
        Resolution
        {draft.state === 'loading' ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            <Loader2 className="mr-1 inline size-3 animate-spin" /> Riley is drafting…
          </span>
        ) : untouchedAlert ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">From the monitoring alert</span>
        ) : draft.state === 'ready' && draft.source === 'riley' ? (
          <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-riley">
            <Sparkles className="size-3" /> Drafted by Riley: review and edit
          </span>
        ) : draft.state === 'ready' && draft.source === 'desk' ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">Already on the ticket</span>
        ) : draft.state === 'ready' && draft.source === 'workflow' ? (
          <span className="ml-2 text-xs font-normal text-muted-foreground">Saved in the guided checklist</span>
        ) : draft.state === 'ready' && draft.source === 'summary' ? (
          <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-riley">
            <Sparkles className="size-3" /> From Riley's case summary: review and edit
          </span>
        ) : null}
        <Textarea
          aria-label="Resolution"
          required
          value={value}
          className="h-12 min-h-12 resize-y"
          placeholder="What was wrong, what we did, and how it ended."
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      </label>
      {draft.state === 'ready' && draft.stripped?.length ? (
        <p className="text-xs text-muted-foreground" data-testid="resolution-stripped">
          Removed from the draft because the resolution prints on the customer invoice:{' '}
          {draft.stripped.map((code) => STRIPPED_LABEL[code] ?? code.replaceAll('_', ' ')).join(', ')}.
        </p>
      ) : null}
      {draft.state === 'ready' && draft.source === 'none' ? (
        <p className="text-xs text-muted-foreground">{draft.detail || 'No draft available; write the resolution yourself.'}</p>
      ) : null}
      {warnings.length ? (
        <ul className="space-y-1 rounded-md bg-warning-soft px-2.5 py-2 text-xs text-warning" aria-label="Resolution warnings">
          {warnings.map((warning) => (
            <li key={warning}>{RESOLUTION_WARNING[warning] ?? `Review warning: ${warning.replaceAll('_', ' ')}`}</li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" size="sm" variant="outline" disabled={draft.state === 'loading'} onClick={onRegenerate}>
          <RefreshCw /> Regenerate
        </Button>
        {reviewRequired ? (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={signedOff}
              onCheckedChange={(v) => onSignedOff(Boolean(v))}
              aria-label={untouchedRiley ? "I've reviewed Riley's resolution" : "I've reviewed this resolution"}
            />
            {untouchedRiley ? "I've reviewed Riley's unchanged resolution" : "I've reviewed this resolution"}
          </label>
        ) : untouchedAlert ? (
          <span className="text-xs text-muted-foreground">
            From the monitoring alert. Edit it if the outcome was different; your edit counts as the review.
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">Your edit counts as the review; no extra tick needed.</span>
        )}
      </div>
    </div>
  )
}
